<template>
  <section class="hero is-default is-bold">
    <div class="hero-body">
      <div class="container">
        <div class="columns" v-if="action">
          <div class="column">
            <div class="columns">
              <div class="column title">
                {{ $t('compTransitionReviewerActionReview.title-action') }}
                <action-tag :tag="action.adminStatus"></action-tag>
              </div>
              <div class="column" style="text-align: end" v-if="isReviewer">
                <b-tooltip
                  :label="
                    $t('compTransitionReviewerActionReview.action-publish')
                  "
                  type="is-black"
                >
                  <b-button
                    :label="$t('compTransitionReviewerActionReview.publish')"
                    @click="confirmPublish(action)"
                    :disabled="!action.isSubmitted()"
                  />
                </b-tooltip>
              </div>
              <div class="column" style="text-align: end" v-if="isReviewer">
                <l-field
                  :label="$t('compTransitionReviewerActionReview.set-reminder')"
                  :tooltip="
                    $t(
                      'compTransitionReviewerActionReview.tooltip-set-reminder'
                    )
                  "
                  label-position="on-border"
                  :type="{ 'is-danger': errors.has('reminder') }"
                  :message="
                    errors.first('reminder')
                      ? $t(
                          'compTransitionReviewerActionReview.reminder-after-now'
                        )
                      : ''
                  "
                >
                  <b-datepicker
                    type="month"
                    v-model="action.reminder"
                    :placeholder="
                      $t(
                        'compTransitionReviewerActionReview.placeholder-set-reminder'
                      )
                    "
                    icon="clock-o"
                    editable
                    trap-focus
                    name="reminder"
                    data-vv-name="reminder"
                    v-validate="'afternow'"
                    @input="reminderChanged"
                  >
                  </b-datepicker>
                </l-field>
              </div>
              <div class="column" style="text-align: end" v-if="!isReviewer">
                <b-tooltip
                  :label="
                    $t('compTransitionReviewerActionReview.action-submit')
                  "
                  type="is-black"
                >
                  <b-button
                    :label="$t('compTransitionReviewerActionReview.submit')"
                    @click="confirmSubmit(action)"
                    :disabled="action.isSubmitted()"
                  />
                </b-tooltip>
              </div>
            </div>
            <!-- deep columns -->
            <b-tabs>
              <b-tab-item
                :label="$t('compTransitionReviewerActionReview.tab-view')"
                icon="eye"
              >
                <action-view-common
                  v-if="entity"
                  :action="action"
                  :entity="entity"
                  :quota="quota"
                  :maxQuota="maxQuota"
                  :actionQuota="actionQuota"
                  :withQuota="true"
                  :generateMailTo="generateMailTo"
                >
                </action-view-common>
              </b-tab-item>
              <b-tab-item
                :label="$t('compTransitionReviewerActionReview.tab-edit')"
                icon="pencil"
              >
                <action-editor
                  :disabled="false"
                  :action="editableAction"
                  :erreur="erreur"
                  :quota="quota"
                  :maxQuota="maxQuota"
                  :actionQuota="actionQuota"
                  @onActionEditorCancel="actionEditorCancel"
                  @onActionEditorSave="actionEditorSave"
                  @onDeleteActionFile="actionFileDelete"
                ></action-editor>
              </b-tab-item>
            </b-tabs>
          </div>

          <!-- display the chat if the action is submitted or published or if there are already some messages
          The last case might happen if the action is modified afer being submitted or published
          -->
          <div class="column" v-if="action">
            <div class="title">
              {{ $t('compTransitionReviewerActionReview.title-messages') }}
            </div>
            <messages
              :messages="messages"
              :owner="[action.displayContact(), action.entity.referent.email]"
              :disabled="action.isDraft()"
              :disabledReason="
                $t('compTransitionReviewerActionReview.please-submit')
              "
              @onMessage="newMessage"
            />
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<script>
import transitionService from '@/services/transitionService.js'
import { Background } from '@/mixins/background.js'
import { axiosRedirectError } from '@/router/utils.js'

import {
  PublicAction,
  Message,
  dateToString
} from '@/models/transition/transition.js'

export default {
  name: 'ActionReview',
  props: {},
  components: {
    'action-view-common': () =>
      import('@/components/transition/ActionViewCommon.vue'),
    messages: () => import('@/components/transition/reviewer/Messages.vue'),
    'action-tag': () => import('@/components/transition/ActionTag.vue'),
    'action-editor': () => import('@/components/transition/actions/Edit.vue'),
    'l-field': () => import('@/components/LField.vue')
  },
  $_veeValidate: {
    validator: 'new'
  },
  mixins: [Background],
  data: function () {
    return {
      erreur: null,
      action: null,
      quota: 0,
      maxQuota: 0,
      actionQuota: 0,
      entity: null,
      messages: [],
      editableAction: null,
      isReviewer: false,
      now: new Date()
    }
  },
  watch: {},
  computed: {
    actionId: function () {
      return this.$route.params.actionId
    }
  },
  methods: {
    reminderChanged (value) {
      this.$validator.validate('reminder', value).then((valid) => {
        if (valid) {
          transitionService.reviewerSaveReminder(
            this.actionId,
            dateToString(value)
          )
        }
      })
    },
    confirmPublish (action) {
      this.$buefy.dialog.confirm({
        title: this.$t('compTransitionReviewerActionReview.confirm-title'),
        message: this.$t('compTransitionReviewerActionReview.ask-publish'),
        confirmText: this.$t('compTransitionReviewerActionReview.validate'),
        cancelText: this.$t('cancel'),
        canCancel: ['escape', 'button', 'outside'],
        onConfirm: () => this.publish(action)
      })
    },
    publish (action) {
      this.bgUI(async () => {
        let data = await transitionService.reviewerTogglePublishAction(
          action.id
        )
        this.action.move(PublicAction.fromDatabase(data))
      })
    },
    confirmSubmit (action) {
      this.$buefy.dialog.confirm({
        title: this.$t(
          'compTransitionReviewerActionReview.confirm-submit-title'
        ),
        message: this.$t('compTransitionReviewerActionReview.ask-submit'),
        confirmText: this.$t('compTransitionReviewerActionReview.validate'),
        cancelText: this.$t('cancel'),
        canCancel: ['escape', 'button', 'outside'],
        onConfirm: () => this.submitAction(action)
      })
    },
    /**
     * Submit an action
     */
    submitAction (action) {
      this.bgUI(
        async () => {
          let data = await transitionService.submitPublicAction(action.id)
          if (data) {
            this.action.move(PublicAction.fromDatabase(data))
          }
          // what if we don't get data here
        },
        this.onSubmitSuccess,
        this.onSubmitError
      )
    },
    newMessage (message) {
      // backend will now who's sending the message
      // once done push the message to the list of messages
      transitionService
        .reviewerSaveMessage(this.action.id, message)
        .then((m) => this.messages.push(Message.fromDatabase(m)))
    },
    generateMailTo (entity, action) {
      if (!entity) {
        return ''
      }
      if (!action) {
        return ''
      }
      let mailTo = entity.referent.email
      let subject = `[labos1point5] Modération de l'action:  ${action.title}`
      // NOTE thunderbird supports html-body
      return `mailto:${mailTo}?subject=${subject}`
    },
    actionEditorCancel (action) {
      // nothing to do ?
    },
    actionEditorSave (event) {
      if (!this.isReviewer) {
        this.$buefy.dialog.confirm({
          title: this.$t(
            'compTransitionReviewerActionReview.confirm-save-title'
          ),
          message: this.$t('compTransitionReviewerActionReview.save-message'),
          confirmText: this.$t('compTransitionReviewerActionReview.validate'),
          canCancel: false,
          onConfirm: () => this.saveAction(event)
        })
      } else {
        this.saveAction(event)
      }
    },
    saveAction (event) {
      let action = event.action
      let files = event.files
      this.validateBgUi(
        async () => {
          let data = await transitionService.reviewerUpdateAction(
            action.toDatabase(),
            files
          )
          if (data) {
            // resync
            this.action.move(PublicAction.fromDatabase(data))
            // editable should be in sync already
            // this seems to get the reactivity right on the upload component
            this.editableAction.files.splice(
              0,
              this.editableAction.files.length
            )
            for (let index in this.action.files) {
              this.editableAction.files.push(this.action.files[index])
            }
            // a bit overkill
            this.updateQuota()
          } else {
            throw Error(
              'Something went wrong when loading back the transition Lab'
            )
          }
        },
        this.onSaveSuccess,
        this.onSaveError
      )
    },
    actionFileDelete (file) {
      this.bgUI(async () => {
        await transitionService.deletePublicActionFile(file)
        // make it consistent with the currentAction copy of the original one

        let index = this.action.files.findIndex((f) => f.id === file.id)
        this.action.files.splice(index, 1)
        this.editableAction.files.splice(index, 1)
      })
    },
    /**
     * Update the global quota. This is used everytime files are touched (added, deleted).
     * This ensure a strong consistency between the displayed quota and the backend.
     */
    updateQuota () {
      transitionService
        .reviewerGetQuota(this.action.entity.id)
        .then((quota) => {
          this.maxQuota = quota.quota_global
          this.actionQuota = quota.quota_action
          // this.quota = (100 * quota.current) / quota.total
          this.quota = quota.current
        })
    }
  },
  mounted () {
    this.bgUI(
      async () => {
        let data = await transitionService.reviewerGetAction(this.actionId)
        this.action = PublicAction.fromDatabase(data)
        this.editableAction = this.action.clone()

        let [isReviewer, messages] = await Promise.all([
          transitionService.hasReviewerPermissions(),
          transitionService.reviewerGetMessages(this.actionId)
        ])

        this.updateQuota()
        this.isReviewer = isReviewer.has_reviewer_permissions

        this.messages = messages.map((m) => Message.fromDatabase(m))

        this.entity = this.action.entity
      },
      undefined,
      axiosRedirectError
    )
  }
}
</script>
